/**
 * 3PL UI kit. Matches the approved mockup (Outfit / Manrope / JetBrains Mono,
 * brand #D96245, warm neutrals, 14px card radius, pill statuses) using the
 * app's Tailwind `ship-*` tokens so light/dark both work.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { useMoney } from '../context/CurrencyContext';
import { fmtMinorText } from '../lib/money';

// ── Status tones (from the mockup's ui.js) ───────────────────────────────────
const TONES = {
  green: 'bg-[#E9F5EF] text-[#2E6A49] dark:bg-[#183324] dark:text-[#7FDCA2]',
  amber: 'bg-[#FCF3E8] text-[#8A5A1F] dark:bg-[#3A2F14] dark:text-[#F0CB75]',
  red: 'bg-[#FCECEC] text-[#B83A3A] dark:bg-[#3A1E1C] dark:text-[#F09A8E]',
  blue: 'bg-[#EAF1F5] text-[#3B6475] dark:bg-[#1C2A33] dark:text-[#9CC3D6]',
  neutral: 'bg-[#EFEBE6] text-[#5E5955] dark:bg-[#2A2724] dark:text-[#C8C2BB]',
  brand: 'bg-[#F0D4CC] text-[#8E3A26] dark:bg-[#3A231C] dark:text-[#F2B8AA]',
};
const TONE_WORDS = {
  green: ['approved', 'received', 'closed', 'delivered', 'paid', 'verified', 'ok', 'in stock', 'active', 'done',
    'restocked', 'accepted', 'resolved', 'handed over', 'shipped', 'fully shipped', 'put away', 'posted', 'billable',
    'saleable', 'eligible', 'completed', 'live', 'generated'],
  blue: ['submitted', 'in transit', 'ready', 'picking', 'packing', 'packed', 'label created', 'awaiting handover',
    'issued', 'quote sent', 'contacted', 'qualified', 'inspecting', 'expected', 'in progress', 'booking requested',
    'onboarding', 'open', 'uploaded', 'allocated', 'on draft', 'invoiced', 'processing', 'out for delivery'],
  amber: ['partially', 'arrived', 'receiving', 'low stock', 'awaiting', 'new', 'draft', 'restock', 'in review',
    'medium', 'planned', 'normal', 'cancellation requested', 'paused', 'quarantine', 'hold', 'backorder'],
  red: ['out of stock', 'rejected', 'overdue', 'disputed', 'failed', 'missing', 'outcome unknown', 'exception', 'high',
    'lost', 'closed with discrepancy', 'blocked', 'missing rate', 'needs investigation', 'short'],
  neutral: ['cancelled', 'not started', 'superseded', 'archived', 'low', 'expired'],
};

export function toneFor(status) {
  const s = String(status || '').toLowerCase().replace(/_/g, ' ');
  for (const tone of ['red', 'neutral', 'green', 'blue', 'amber']) {
    if (TONE_WORDS[tone].some((w) => s === w || s.startsWith(w))) return tone;
  }
  return 'neutral';
}

export function humanize(s) {
  const t = String(s || '').replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
}

export function StatusPill({ status, label, tone, className = '', testId }) {
  const t = tone || toneFor(label || status);
  return (
    <span data-testid={testId}
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold whitespace-nowrap ${TONES[t]} ${className}`}>
      {label || humanize(status)}
    </span>
  );
}

// ── Text bits ────────────────────────────────────────────────────────────────
export const Mono = ({ children, className = '' }) => (
  <span className={`font-mono text-[12.5px] ${className}`}>{children}</span>
);

/**
 * Integer minor units in `currency` → the viewer's display currency (converted
 * through the daily INR rates; INR viewers and staff see INR). Components that
 * call this outside a DataTable should call `useMoney()` so they re-render when
 * the display currency or rates change.
 */
export function fmtMoney(minor, currency = 'INR') {
  return fmtMinorText(minor, currency);
}

export function fmtDate(iso, withTime = true) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('en-IN', withTime
    ? { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }
    : { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
}

// ── Layout pieces ────────────────────────────────────────────────────────────
export function PageHeader({ eyebrow, title, subtitle, actions, children, testId }) {
  return (
    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" data-testid={testId}>
      <div className="min-w-0">
        {eyebrow && <div className="mb-1 text-[11px] font-bold uppercase tracking-[0.14em] text-ship-subtle">{eyebrow}</div>}
        <h1 className="font-heading text-[26px] font-bold leading-tight text-ship-text sm:text-[28px]">{title}</h1>
        {subtitle && <p className="mt-1 max-w-[70ch] text-sm text-ship-muted">{subtitle}</p>}
        {children}
      </div>
      {actions && <div className="flex flex-wrap gap-2 sm:justify-end">{actions}</div>}
    </div>
  );
}

export function Card({ title, actions, children, className = '', bodyClassName = 'p-4 sm:p-5', testId }) {
  return (
    <section className={`rounded-[14px] border border-ship-border bg-ship-card ${className}`} data-testid={testId}>
      {(title || actions) && (
        <div className="flex items-center justify-between gap-3 border-b border-ship-border px-4 py-3 sm:px-5">
          {title && <h2 className="font-heading text-[17px] font-semibold text-ship-text">{title}</h2>}
          {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
        </div>
      )}
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}

export function Kpi({ label, value, hint, to, testId }) {
  const body = (
    <div className="rounded-[14px] border border-ship-border bg-ship-card p-4 transition hover:border-ship-border-strong"
      data-testid={testId}>
      <div className="text-xs font-bold text-ship-muted">{label}</div>
      <div className="mt-1 font-heading text-[30px] font-bold tabular-nums text-ship-text">{value ?? '—'}</div>
      {hint && <div className="mt-1 text-xs text-ship-subtle">{hint}</div>}
    </div>
  );
  return to ? <Link to={to} className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D96245] rounded-[14px]">{body}</Link> : body;
}

const BANNER = {
  info: 'border-[#C9DCE6] bg-[#EAF1F5] text-[#2C4A57] dark:border-[#2F4652] dark:bg-[#1C2A33] dark:text-[#CFE3EC]',
  warn: 'border-[#F0D9B5] bg-[#FCF3E8] text-[#6B4716] dark:border-[#5A4520] dark:bg-[#3A2F14] dark:text-[#F5DDA6]',
  error: 'border-[#F2C4C4] bg-[#FCECEC] text-[#7A2323] dark:border-[#5A2A26] dark:bg-[#3A1E1C] dark:text-[#F6B8AE]',
  success: 'border-[#BFE0CC] bg-[#E9F5EF] text-[#2E6A49] dark:border-[#2A5A3C] dark:bg-[#183324] dark:text-[#9EE3B8]',
};

export function Banner({ tone = 'info', title, children, actions, testId, className = '' }) {
  return (
    <div role={tone === 'error' ? 'alert' : 'status'} data-testid={testId}
      className={`rounded-xl border px-4 py-3 text-sm ${BANNER[tone]} ${className}`}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          {title && <div className="font-bold">{title}</div>}
          {children && <div className={title ? 'mt-0.5' : ''}>{children}</div>}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>}
      </div>
    </div>
  );
}

// ── Buttons and fields ───────────────────────────────────────────────────────
const BTN = {
  primary: 'bg-[#D96245] text-white hover:bg-[#C05238] active:bg-[#A6432D] border border-transparent',
  secondary: 'bg-ship-card text-ship-text border border-ship-border-strong hover:bg-ship-hover',
  ghost: 'bg-transparent text-ship-text border border-transparent hover:bg-ship-hover',
  danger: 'bg-[#D94545] text-white hover:bg-[#B83A3A] border border-transparent',
};

export function Btn({ variant = 'secondary', busy, busyLabel, children, className = '', size = 'md', ...rest }) {
  const sz = size === 'lg' ? 'h-14 px-5 text-base rounded-[14px]' : size === 'sm' ? 'h-8 px-3 text-xs rounded-lg' : 'h-10 px-4 text-sm rounded-[10px]';
  return (
    <button type="button" {...rest} disabled={rest.disabled || busy} aria-busy={busy || undefined}
      className={`inline-flex items-center justify-center gap-2 font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D96245] focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 ${sz} ${BTN[variant]} ${className}`}>
      {busy && <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden />}
      {busy && busyLabel ? busyLabel : children}
    </button>
  );
}

export function BtnLink({ to, variant = 'secondary', children, className = '', ...rest }) {
  return (
    <Link to={to} {...rest}
      className={`inline-flex h-10 items-center justify-center gap-2 rounded-[10px] px-4 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D96245] ${BTN[variant]} ${className}`}>
      {children}
    </Link>
  );
}

let fieldSeq = 0;
function useFieldId(id) {
  const ref = useRef(id || `fl-field-${++fieldSeq}`);
  return ref.current;
}

const INPUT = 'w-full rounded-[10px] border bg-ship-card px-3 text-sm text-ship-text placeholder:text-ship-subtle focus:outline-none focus:ring-2 focus:ring-[#D96245]';

export function Field({ label, hint, error, required, children, id }) {
  const fid = useFieldId(id);
  const child = React.isValidElement(children)
    ? React.cloneElement(children, {
      id: children.props.id || fid,
      'aria-invalid': error ? true : undefined,
      'aria-describedby': error ? `${fid}-err` : hint ? `${fid}-hint` : undefined,
    })
    : children;
  return (
    <div className="flex min-w-0 flex-col gap-1">
      {label && (
        <label htmlFor={children?.props?.id || fid} className="text-[13px] font-bold text-ship-text">
          {label}{required && <span className="text-[#D94545]"> *</span>}
        </label>
      )}
      {child}
      {hint && !error && <div id={`${fid}-hint`} className="text-xs text-ship-subtle">{hint}</div>}
      {error && <div id={`${fid}-err`} className="text-xs font-semibold text-[#B83A3A]">{error}</div>}
    </div>
  );
}

export const Input = React.forwardRef(function Input({ className = '', invalid, ...rest }, ref) {
  return <input ref={ref} {...rest} className={`${INPUT} h-10 ${invalid || rest['aria-invalid'] ? 'border-[#D94545]' : 'border-ship-border-strong'} ${className}`} />;
});

export function Select({ className = '', children, ...rest }) {
  return (
    <select {...rest} className={`${INPUT} h-10 ${rest['aria-invalid'] ? 'border-[#D94545]' : 'border-ship-border-strong'} ${className}`}>
      {children}
    </select>
  );
}

export function Textarea({ className = '', ...rest }) {
  return <textarea {...rest} className={`${INPUT} min-h-[88px] py-2 ${rest['aria-invalid'] ? 'border-[#D94545]' : 'border-ship-border-strong'} ${className}`} />;
}

// ── States ───────────────────────────────────────────────────────────────────
export function Loading({ rows = 4, testId = 'fl-loading' }) {
  return (
    <div className="space-y-3" aria-busy="true" aria-label="Loading" data-testid={testId}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-12 animate-pulse rounded-xl bg-ship-sidebar" />
      ))}
    </div>
  );
}

export function ErrorState({ text, onRetry, testId = 'fl-error' }) {
  return (
    <Banner tone="error" title="Couldn’t load this" testId={testId}
      actions={onRetry && <Btn size="sm" onClick={onRetry}>Try again</Btn>}>
      {text}
    </Banner>
  );
}

export function EmptyState({ title, body, action, testId = 'fl-empty' }) {
  return (
    <div className="rounded-[14px] border border-dashed border-ship-border-strong px-6 py-10 text-center" data-testid={testId}>
      <div className="font-heading text-[17px] font-semibold text-ship-text">{title}</div>
      {body && <p className="mx-auto mt-1 max-w-[52ch] text-sm text-ship-muted">{body}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

/**
 * Loading → failed (retry; stale data stays visible) → empty → content.
 * `isEmpty(data)` decides the empty case; `emptyView` is what to show then.
 */
export function Async({ state, children, isEmpty, emptyView, loadingRows }) {
  if (state.loading && !state.data) return <Loading rows={loadingRows} />;
  if (state.error && !state.data) return <ErrorState text={state.errorText} onRetry={state.reload} />;
  const empty = isEmpty ? isEmpty(state.data) : false;
  return (
    <>
      {state.error && <div className="mb-3"><ErrorState text={state.errorText} onRetry={state.reload} /></div>}
      {empty ? emptyView : children(state.data)}
    </>
  );
}

// ── Tabs ─────────────────────────────────────────────────────────────────────
export function Tabs({ tabs, value, onChange, testId = 'fl-tabs' }) {
  return (
    <div className="-mx-1 mb-4 flex gap-1 overflow-x-auto px-1 pb-1" role="tablist" data-testid={testId}>
      {tabs.map((t) => {
        const on = t.id === value;
        return (
          <button key={t.id} type="button" role="tab" aria-selected={on} onClick={() => onChange(t.id)}
            data-testid={`${testId}-${t.id}`}
            className={`h-9 shrink-0 rounded-full px-3.5 text-[13px] font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D96245] ${on ? 'bg-[#2C2825] text-white dark:bg-[#F5F2EF] dark:text-[#121110]' : 'bg-ship-sidebar text-ship-muted hover:text-ship-text'}`}>
            {t.label}{t.count !== undefined && <span className="ml-1.5 tabular-nums opacity-70">{t.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

// ── Table that becomes cards under 640px ─────────────────────────────────────
/**
 * columns: [{ key, label, render?(row), align?, className?, hideOnMobile?, primary? }]
 * One column can be `primary` — it becomes the card title on phones.
 */
export function DataTable({ columns, rows, rowKey, onRowClick, testId = 'fl-table', rowTestId, footer }) {
  useMoney(); // money cells re-render when the display currency changes
  const primary = columns.find((c) => c.primary) || columns[0];
  const cell = (c, r) => (c.render ? c.render(r) : (r[c.key] ?? '—'));
  return (
    <div data-testid={testId}>
      <div className="hidden overflow-x-auto rounded-[14px] border border-ship-border bg-ship-card sm:block">
        <table className="w-full text-sm">
          <thead className="bg-ship-sidebar">
            <tr>
              {columns.map((c) => (
                <th key={c.key} scope="col"
                  className={`whitespace-nowrap px-4 py-2.5 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-ship-subtle ${c.align === 'right' ? 'text-right' : ''}`}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-ship-border">
            {rows.map((r) => (
              <tr key={rowKey(r)} data-testid={rowTestId ? rowTestId(r) : undefined}
                onClick={onRowClick ? () => onRowClick(r) : undefined}
                className={onRowClick ? 'cursor-pointer hover:bg-ship-hover' : ''}>
                {columns.map((c) => (
                  <td key={c.key} className={`px-4 py-3 align-top text-ship-text ${c.align === 'right' ? 'text-right tabular-nums' : ''} ${c.className || ''}`}>
                    {cell(c, r)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ul className="space-y-3 sm:hidden">
        {rows.map((r) => (
          <MobileCard key={rowKey(r)} r={r} columns={columns} primary={primary} cell={cell}
            onClick={onRowClick} testId={rowTestId ? rowTestId(r) : undefined} />
        ))}
      </ul>
      {footer}
    </div>
  );
}

function MobileCard({ r, columns, primary, cell, onClick, testId }) {
  const [more, setMore] = useState(false);
  const rest = columns.filter((c) => c !== primary);
  const shown = rest.filter((c) => !c.hideOnMobile);
  const hidden = rest.filter((c) => c.hideOnMobile);
  return (
    <li data-testid={testId} className="rounded-[14px] border border-ship-border bg-ship-card p-4">
      <div className="mb-2 font-semibold text-ship-text">
        {onClick ? (
          <button type="button" onClick={() => onClick(r)} className="text-left underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D96245]">
            {cell(primary, r)}
          </button>
        ) : cell(primary, r)}
      </div>
      <dl className="grid grid-cols-[minmax(0,40%)_1fr] gap-x-3 gap-y-1.5 text-sm">
        {(more ? [...shown, ...hidden] : shown).map((c) => (
          <React.Fragment key={c.key}>
            <dt className="text-ship-subtle">{c.label}</dt>
            <dd className="min-w-0 break-words text-ship-text">{cell(c, r)}</dd>
          </React.Fragment>
        ))}
      </dl>
      {hidden.length > 0 && (
        <button type="button" onClick={() => setMore((v) => !v)} aria-expanded={more}
          className="mt-2 text-xs font-semibold text-[#B44B33] dark:text-[#F2B8AA]">
          {more ? 'Fewer details ▴' : `More details (${hidden.length}) ▾`}
        </button>
      )}
    </li>
  );
}

export function Pager({ skip, limit, total, onPage, testId = 'fl-pager' }) {
  if (!total || total <= limit) return null;
  const from = skip + 1;
  const to = Math.min(skip + limit, total);
  return (
    <div className="mt-3 flex items-center justify-between text-sm text-ship-muted" data-testid={testId}>
      <span>Showing {from}–{to} of {total}</span>
      <div className="flex gap-2">
        <Btn size="sm" disabled={skip === 0} onClick={() => onPage(Math.max(0, skip - limit))}>Previous</Btn>
        <Btn size="sm" disabled={to >= total} onClick={() => onPage(skip + limit)}>Next</Btn>
      </div>
    </div>
  );
}

// ── Dialogs ──────────────────────────────────────────────────────────────────
/**
 * Confirm dialog with optional input. `context` shows "MERCHANT: …" like the
 * mockup. onConfirm(value) may return a promise; errors render inline.
 */
export function Confirm({ open, onOpenChange, title, lines = [], context, input, confirmLabel = 'Confirm',
  destructive, busy, error, onConfirm, testId = 'fl-confirm', children }) {
  const [value, setValue] = useState('');
  useEffect(() => { if (open) setValue(input?.initial || ''); }, [open, input?.initial]);
  const needs = input && input.required && value.trim().length < (input.minLength || 1);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={testId} className="max-w-[460px]">
        <DialogHeader>
          {context && <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-ship-subtle">{context}</div>}
          <DialogTitle className="font-heading">{title}</DialogTitle>
          {lines.length > 0 && (
            <DialogDescription asChild>
              <div className="space-y-1.5 text-sm text-ship-muted">{lines.map((l, i) => <p key={i}>{l}</p>)}</div>
            </DialogDescription>
          )}
        </DialogHeader>
        {children}
        {input && (
          <Field label={input.label} hint={input.hint} required={input.required}>
            {input.multiline
              ? <Textarea value={value} onChange={(e) => setValue(e.target.value)} placeholder={input.placeholder} data-testid={`${testId}-input`} />
              : <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder={input.placeholder}
                type={input.type || 'text'} data-testid={`${testId}-input`} />}
          </Field>
        )}
        {error && <Banner tone="error">{error}</Banner>}
        <DialogFooter className="gap-2">
          <Btn onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Btn>
          <Btn variant={destructive ? 'danger' : 'primary'} busy={busy} disabled={needs}
            onClick={() => onConfirm(value)} data-testid={`${testId}-ok`}>{confirmLabel}</Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Scan input (floor) ───────────────────────────────────────────────────────
/** Large input that keeps focus for barcode scanners; Enter submits. */
export function ScanInput({ label = 'Scan or type', placeholder, onScan, autoFocus = true, disabled, testId = 'fl-scan' }) {
  const ref = useRef(null);
  const [v, setV] = useState('');
  useEffect(() => { if (autoFocus && !disabled) ref.current?.focus(); }, [autoFocus, disabled]);
  return (
    <form onSubmit={(e) => { e.preventDefault(); const code = v.trim(); if (code) { onScan(code); setV(''); } }}
      className="flex gap-2" data-testid={testId}>
      <label className="sr-only" htmlFor={`${testId}-input`}>{label}</label>
      <input id={`${testId}-input`} ref={ref} value={v} onChange={(e) => setV(e.target.value)} disabled={disabled}
        placeholder={placeholder || label} autoComplete="off" autoCapitalize="characters" spellCheck={false}
        inputMode="text" enterKeyHint="go" data-scan="true"
        className="h-14 min-w-0 flex-1 rounded-[14px] border-2 border-[#2C2825] bg-white px-4 font-mono text-lg text-[#1E1B19] focus:outline-none focus:ring-4 focus:ring-[#D96245]/40 dark:border-[#F5F2EF] dark:bg-[#22201E] dark:text-[#F5F2EF]" />
      <Btn type="submit" variant="primary" size="lg" disabled={disabled || !v.trim()} data-testid={`${testId}-go`}>Go</Btn>
    </form>
  );
}

// ── File upload with honest states ───────────────────────────────────────────
/** Upload a file immediately on choose. Only a finished upload counts. */
export function UploadButton({ label = 'Upload', accept, onUpload, onDone, testId = 'fl-upload', capture }) {
  const [state, setState] = useState({ status: 'idle', name: '', error: '' });
  const inputRef = useRef(null);
  const pick = async (file) => {
    if (!file) return;
    setState({ status: 'uploading', name: file.name, error: '' });
    try {
      const doc = await onUpload(file);
      setState({ status: 'uploaded', name: file.name, error: '' });
      if (onDone) onDone(doc);
    } catch (err) {
      setState({ status: 'failed', name: file.name, error: err?.response?.data?.detail || 'Upload failed. Try again.' });
    }
  };
  return (
    <div className="flex flex-col gap-1" data-testid={testId}>
      <input ref={inputRef} type="file" accept={accept} capture={capture} className="sr-only" id={`${testId}-file`}
        onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} />
      <div className="flex flex-wrap items-center gap-2">
        <Btn onClick={() => inputRef.current?.click()} busy={state.status === 'uploading'} busyLabel="Uploading…">
          {state.status === 'failed' ? 'Retry upload' : label}
        </Btn>
        {state.status === 'uploaded' && <StatusPill status="uploaded" label={`Uploaded: ${state.name}`} />}
      </div>
      {state.status === 'failed' && <div className="text-xs font-semibold text-[#B83A3A]" role="alert">{state.error}</div>}
    </div>
  );
}

/** Small key/value list. items: [[label, value], ...] */
export function KV({ items, className = '' }) {
  return (
    <dl className={`grid grid-cols-[minmax(0,42%)_1fr] gap-x-3 gap-y-2 text-sm ${className}`}>
      {items.filter(Boolean).map(([k, v]) => (
        <React.Fragment key={k}>
          <dt className="text-ship-subtle">{k}</dt>
          <dd className="min-w-0 break-words text-ship-text">{v ?? '—'}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

/** Numbered tiles used across the floor and order screens. */
export function Tiles({ items, testId }) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid={testId}>
      {items.map(([label, value, tone]) => (
        <div key={label} className={`rounded-xl border border-ship-border p-3 ${tone === 'brand' ? 'bg-[#F0D4CC]/40' : 'bg-ship-card'}`}>
          <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-ship-subtle">{label}</div>
          <div className="mt-0.5 font-heading text-[26px] font-bold tabular-nums text-ship-text">{value}</div>
        </div>
      ))}
    </div>
  );
}
